/**
 * Sinalização WebRTC e negociação perfeita.
 *
 * O que mora aqui é só decisão, sem `RTCPeerConnection` nenhuma: dá pra
 * testar a parte difícil no Vitest, sem navegador. O wrapper que de fato
 * mexe na conexão vive em apps/web e consulta estas funções.
 */
import { z } from 'zod'
import { roleSchema } from './schemas.js'

export const sessionDescriptionSchema = z.object({
  type: z.enum(['offer', 'answer', 'pranswer', 'rollback']),
  sdp: z.string().max(100_000),
})

export const iceCandidateSchema = z.object({
  candidate: z.string().max(4000),
  sdpMid: z.string().max(64).nullish(),
  sdpMLineIndex: z.number().int().min(0).max(64).nullish(),
  usernameFragment: z.string().max(256).nullish(),
})

export const signalSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('description'), description: sessionDescriptionSchema }),
  z.object({ kind: z.literal('candidate'), candidate: iceCandidateSchema }),
  /** A outra ponta desligou a mídia de propósito. */
  z.object({ kind: z.literal('bye') }),
  /**
   * "Meu peer é novo, recomece comigo." Sai de quem acabou de montar a
   * conexão; a ponta não educada responde com oferta, a educada nunca oferece.
   */
  z.object({ kind: z.literal('renegotiate') }),
])

/** O que o servidor entrega, com quem mandou. */
export const routedSignalSchema = z.object({
  from: roleSchema,
  signal: signalSchema,
})

export type SessionDescriptionPayload = z.infer<typeof sessionDescriptionSchema>
export type IceCandidatePayload = z.infer<typeof iceCandidateSchema>
export type Signal = z.infer<typeof signalSchema>
export type RoutedSignal = z.infer<typeof routedSignalSchema>

/**
 * Negociação perfeita, do documento do WebRTC, com uma ponta só oferecendo.
 *
 * A terapeuta é a educada e só responde: com as duas oferecendo na admissão,
 * a educada desfazia a própria oferta e o Chrome deixava o ICE dela parado,
 * sem candidato. A regra de colisão continua aqui para a oferta que cruza
 * com um restart: a educada engole a dela e aceita a de fora; a mal-educada
 * ignora a de fora e segue com a sua.
 */
export interface NegotiationContext {
  polite: boolean
  /** Estamos no meio de criar e mandar uma oferta. */
  makingOffer: boolean
  signalingState: 'stable' | 'have-local-offer' | 'have-remote-offer' | 'closed' | string
  /** Estamos aplicando uma resposta remota agora. */
  isSettingRemoteAnswerPending: boolean
}

export type DescriptionAction =
  /** Descarta: colisão de ofertas e nós somos a ponta mal-educada. */
  | 'ignore'
  /** Aplica a descrição remota e para por aí. */
  | 'accept'
  /** Aplica a descrição remota e responde. */
  | 'accept-and-answer'

export function decideOnDescription(
  context: NegotiationContext,
  type: SessionDescriptionPayload['type'],
): DescriptionAction {
  const readyForOffer =
    !context.makingOffer &&
    (context.signalingState === 'stable' || context.isSettingRemoteAnswerPending)
  const offerCollision = type === 'offer' && !readyForOffer

  if (offerCollision && !context.polite) return 'ignore'
  // A ponta educada aceita a oferta de fora mesmo em colisão. O rollback da
  // oferta local sai de graça no setRemoteDescription dos navegadores atuais.
  if (type === 'offer') return 'accept-and-answer'
  return 'accept'
}

/** Quem é educado. Combinado nos dois lados, sem negociar. */
export function isPolite(role: z.infer<typeof roleSchema>): boolean {
  return role === 'therapist'
}

/** Estado da mídia, o que a interface mostra. */
export type MediaPhase =
  | 'idle'
  | 'connecting'
  /** Conectado, mídia fluindo. */
  | 'connected'
  /** Caiu e estamos tentando recuperar sem derrubar a sessão. */
  | 'recovering'
  | 'failed'
  | 'closed'

export type MediaEvent =
  | 'start'
  | 'ice-checking'
  | 'ice-connected'
  | 'ice-disconnected'
  | 'ice-failed'
  | 'restart'
  | 'give-up'
  | 'close'

/**
 * Máquina de fase da mídia.
 *
 * `disconnected` não é falha: o ICE volta sozinho com frequência, ainda mais
 * em celular trocando de torre. Por isso ele cai em `recovering` e não em
 * `failed`, e só `give-up` fecha a porta.
 */
export function nextMediaPhase(current: MediaPhase, event: MediaEvent): MediaPhase {
  if (event === 'close') return 'closed'
  if (current === 'closed') return 'closed'

  switch (event) {
    case 'start':
      return current === 'connected' ? 'connected' : 'connecting'
    case 'ice-checking':
      return current === 'connected' ? 'connected' : 'connecting'
    case 'ice-connected':
      return 'connected'
    case 'ice-disconnected':
      return current === 'idle' ? 'idle' : 'recovering'
    case 'ice-failed':
      return 'recovering'
    case 'restart':
      return current === 'idle' ? 'idle' : 'recovering'
    case 'give-up':
      return 'failed'
  }
}

/** Quantas tentativas de reinício de ICE antes de desistir. */
export const MAX_ICE_RESTARTS = 5

/**
 * Espera antes da próxima tentativa, com teto de 8 s.
 *
 * O sorteio no fim separa as duas pontas quando as duas falham no mesmo
 * instante, que é o caso comum, porque em geral o que caiu foi a rede no
 * meio. Sem ele as duas reiniciam juntas, colidem, e a colisão vira a
 * próxima falha.
 */
export function iceRestartDelayMs(attempt: number, random: () => number = Math.random): number {
  const base = Math.min(500 * 2 ** Math.max(0, attempt), 8000)
  return Math.round(base * (0.7 + 0.6 * random()))
}

export function shouldGiveUp(attempt: number): boolean {
  return attempt >= MAX_ICE_RESTARTS
}
